/**
 * Allowed HCP slug format, mirroring the backend `slug` validator.
 *
 * The separator is spelled `(?:_|-)` rather than `[_-]` on purpose. The native HTML
 * `pattern` attribute is compiled with the RegExp `v` flag, where a bare `-` inside
 * a character class is a syntax error — and a pattern that fails to compile is
 * silently ignored, so the validation would never run. `[-_]` fails the same way,
 * and `[_\-]` works but trips ESLint's no-useless-escape.
 *
 * Flags must not be added either; CTextField extracts the source with a
 * `/^\/(.*)\/$/` match, which returns null for a flagged regex.
 */
export const HCP_SLUG_PATTERN = /^[a-z0-9]+((?:_|-)[a-z0-9]+)*$/;

export interface Hcp {
  id: number;
  name: string;
  email?: string;
  phone_number?: string;
  address?: string;
  category?: string;
  iana_timezone?: string;
  slug?: string;
  admits_patients?: boolean;
  health_care_organization_id?: number;
  created_at?: string;
  deleted_at?: string;
}

export interface HcpListItem {
  id: number;
  name: string;
}

export interface HcpListResponse {
  items: HcpListItem[];
  metadata: {
    page: number;
    next: number | null;
    prev: number | null;
    page_items: number;
    total_count: number;
  };
}

export interface ServiceCategory {
  id: number;
  name: string;
  description?: string;
  health_care_provider_id: number;
}

export interface ServiceItem {
  id: number;
  name: string;
  description?: string;
  duration: number;
  mode: string;
  purpose: string;
  payment_methods?: string[];
  opening_hours?: number;
  closing_hours?: number;
  no_earlier_than?: number;
  no_later_than?: number;
  capacity?: number;
  health_questionnaire_type?: string;
  bookable_by_patients: boolean;
  category?: ServiceCategory;
  health_care_provider_id: number;
  health_care_professional_ids?: number[];
}

export interface ProfessionalItem {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface CreateServiceInput {
  name: string;
  description: string;
  duration: number;
  mode: string;
  purpose: string;
  payment_methods?: string[];
  opening_hours?: number;
  closing_hours?: number;
  no_earlier_than?: number;
  no_later_than?: number;
  capacity?: number;
  health_questionnaire_type?: string;
  bookable_by_patients?: boolean;
  health_care_provider_service_category_id?: number;
  health_care_professional_ids?: number[];
}

export interface CreateServiceCategoryInput {
  name: string;
  description?: string;
}
